import { computed } from "vue";
import { useStore } from "vuex";
import { useComposition } from "@/lib/mypage/useMypage";

export interface Menu {
  title: string;
  link: string;
  children?: Menu[];
  isRender?: boolean;
}

/** 로그인 이후 표시 */
const renderAfterLogin = () => {
  const store = useStore();
  const isAuthenticated = computed(
    () => !!store.getters["auth/getSessionToken"]()
  );

  return isAuthenticated.value ? true : false;
};

type router = { title: string; link: string };

export const useGnbMenu = () => {
  const store = useStore();
  const client = computed(() => store.getters["auth/getClientInfo"]());
  const isAuthenticated = computed(
    () => !!store.getters["auth/getSessionToken"]()
  );

  // 국제우편 사용여부
  const isGlobalMail = computed(() => {
    const user = client.value;
    const ems = [!!user.ems, !!user.ems.enabled].every((v) => v);

    const dhl = [!!user.dhl, !!user.dhl.enabled].every((v) => v);
    return ems || dhl;
  });

  // 등기 사용 여부
  const isMail = computed(() => {
    const user = client.value;
    const registryMail = [
      !!user.registeredMail,
      !!user.registeredMail.enabled,
    ].every((v) => v);

    const expressMail = [!!user.expressMail, !!user.expressMail.enabled].every(
      (v) => v
    );
    return expressMail || registryMail;
  });

  const isQuick = computed(() => {
    const user = client.value;
    const checkList = [!!user.quick, !!user.quick.enabled];
    return checkList.every((v) => v);
  });

  const isParcel = computed(() => {
    const user = client.value;
    const checkList = [!!user.parcel, !!user.parcel.enabled];
    return checkList.every((v) => v);
  });

  /**
   * 오더상세 하위 라우터
   */
  const getOrderRouters = computed(() => {
    const defaultRouter: router[] = [];

    if (!isAuthenticated.value) return defaultRouter; // 비로그인 인 경우

    if (isQuick.value)
      defaultRouter.push({
        title: "퀵접수",
        link: "/outbound/quick",
      });

    if (isParcel.value) {
      defaultRouter.push(
        {
          title: "택배접수",
          link: "/outbound/parcel",
        },
        { title: "다량택배접수", link: "/outbound/mass-parcel" }
      );
    }

    if (isMail.value)
      defaultRouter.push({
        title: "등기접수",
        link: "/outbound/registry",
      });

    if (isGlobalMail.value) {
      defaultRouter.push({
        title: "국제우편접수",
        link: "/outbound/global",
      });
    }

    return defaultRouter;
  });

  /**
   * 사용자 메뉴
   */
  const getMenu = computed<Menu[]>(() => {
    const menu = [
      {
        title: "주문접수",
        link:
          isAuthenticated.value && getOrderRouters.value.length
            ? getOrderRouters.value[0].link
            : "/outbound/complete",
        children:
          isAuthenticated.value && getOrderRouters.value.length
            ? getOrderRouters.value
            : [],
      },
      {
        title: "배송조회",
        link: "/tracking/outbound",
        children: [
          { title: "발송내역조회", link: "/tracking/outbound" },
          { title: "수신내역조회", link: "/tracking/inbound" },
        ],
      },
      {
        title: "서비스소개",
        link: "/service",
        children: [
          { title: "서비스소개", link: "/service" },
          { title: "공지사항", link: "/help/notice" },
          { title: "FAQ", link: "/help/faq" },
        ],
      },
      {
        title: "고객혜택",
        link: "/client/benefit",
      },
    ];

    return menu;
  });

  const getMypage = computed(() => {
    if (!isAuthenticated.value) return [];
    const { userUnitPrivilege } = useComposition();
    const unitTitle = userUnitPrivilege.value.unitAuth === 'unitAdmin' ? '유닛' : '그룹';
    return [
      {
        title: "마이페이지",
        link: "/mypage",
        children: [
          { title: "회원정보", link: "/mypage/profile" },
          { title: "개인결제수단", link: "/mypage/billing" },
          { title: unitTitle, link: "/mypage/unit" },
        ],
      },
    ];
  });

  return {
    getMenu,
    getMypage,
  };
};
